import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../services/api';
import Icon from '../../components/AppIcon';

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const passwordRules = [
  { label: 'At least 12 characters', test: (p) => p?.length >= 12 },
  { label: 'At least 1 uppercase letter', test: (p) => /[A-Z]/?.test(p) },
  { label: 'At least 1 number', test: (p) => /[0-9]/?.test(p) },
  { label: 'At least 1 special character', test: (p) => /[^A-Za-z0-9]/?.test(p) },
];

const AuthBackground = ({ children }) => (
  <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden"
    style={{ background: 'linear-gradient(135deg, #e8e4f8 0%, #d4d8f5 25%, #c8d4f0 50%, #b8cef0 75%, #a8c8ee 100%)' }}>
    <div className="absolute top-0 left-0 w-full h-full pointer-events-none overflow-hidden">
      <svg className="absolute bottom-0 left-0 w-full" viewBox="0 0 1440 320" preserveAspectRatio="none" style={{height:'45%'}}>
        <path fill="rgba(100,120,220,0.18)" d="M0,160L60,170.7C120,181,240,203,360,197.3C480,192,600,160,720,154.7C840,149,960,171,1080,181.3C1200,192,1320,192,1380,192L1440,192L1440,320L1380,320C1320,320,1200,320,1080,320C960,320,840,320,720,320C600,320,480,320,360,320C240,320,120,320,60,320L0,320Z"/>
      </svg>
      <svg className="absolute bottom-0 right-0 w-3/4" viewBox="0 0 800 400" preserveAspectRatio="none" style={{height:'55%'}}>
        <path fill="rgba(80,100,200,0.22)" d="M800,100L700,120C600,140,400,180,200,190C0,200,0,180,0,160L0,400L800,400Z"/>
      </svg>
      <div className="absolute top-8 right-12 opacity-20" style={{color:'#7c6fe0'}}>
        {[...Array(5)]?.map((_,r)=>(
          <div key={r} className="flex space-x-3 mb-3">
            {[...Array(5)]?.map((_,c)=><div key={c} className="w-1.5 h-1.5 rounded-full bg-current"/>)}
          </div>
        ))}
      </div>
    </div>
    <div className="w-full max-w-lg relative z-10 py-6">
      {children}
    </div>
  </div>
);

const SignUpPage = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    organization_name: '', email_address: '', password: '', confirm_password: '',
    first_name: '', last_name: '', phone: ''
  });
  const [touched, setTouched] = useState({});
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const emailValid = emailRegex?.test(form?.email_address);
  const passwordValid = passwordRules?.every(r => r?.test(form?.password));
  const confirmValid = form?.confirm_password?.length > 0 && form?.password === form?.confirm_password;
  const firstNameValid = form?.first_name?.trim()?.length > 0;
  const lastNameValid = form?.last_name?.trim()?.length > 0;
  const orgNameValid = form?.organization_name?.trim()?.length > 0;
  const phoneValid = form?.phone?.trim()?.length > 0;

  const allValid = firstNameValid && lastNameValid && orgNameValid && emailValid && phoneValid && passwordValid && confirmValid;

  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e?.target?.name]: e?.target?.value }));
    setError('');
  };

  const handleBlur = (e) => {
    setTouched(prev => ({ ...prev, [e?.target?.name]: true }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setTouched({
      first_name: true, last_name: true, organization_name: true,
      email_address: true, phone: true, password: true, confirm_password: true
    });
    if (!allValid) return;
    setLoading(true);
    try {
      await apiService?.signUp({
        organization_name: form?.organization_name,
        email_address: form?.email_address,
        password: form?.password,
        first_name: form?.first_name,
        last_name: form?.last_name,
        phone: form?.phone,
      });
      setSuccess('Account created successfully! Please login to continue.');
      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      setError(err?.response?.data?.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getFieldClass = (isValid, isTouched) => {
    if (!isTouched) return 'border-[#dde1f5] focus:border-[#5b4fcf] focus:ring-[#5b4fcf]/20';
    return isValid
      ? 'border-green-400 ring-1 ring-green-400/30' :'border-red-400 ring-1 ring-red-400/30';
  };

  const FieldError = ({ show, message }) =>
    show ? (
      <p className="text-xs text-red-500 flex items-center space-x-1 mt-1">
        <Icon name="AlertCircle" size={12} className="flex-shrink-0" />
        <span>{message}</span>
      </p>
    ) : null;

  const SuccessIcon = ({ show }) =>
    show ? <Icon name="CheckCircle2" size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-green-500" /> : null;

  return (
    <AuthBackground>
      <div className="text-center mb-7">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-4 shadow-lg"
          style={{ background: 'linear-gradient(135deg, #5b4fcf 0%, #4a6bc4 100%)' }}>
          <Icon name="UserPlus" size={30} color="white" />
        </div>
        <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Create Account</h1>
        <p className="text-gray-500 mt-2 text-sm">Register your organization</p>
      </div>

      <div className="bg-white rounded-2xl shadow-xl p-8 border border-white/60">
        {success ? (
          <div className="text-center py-8 space-y-4">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-green-50 rounded-full">
              <Icon name="CheckCircle" size={28} className="text-green-500" />
            </div>
            <p className="text-gray-800 font-semibold">{success}</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* First & Last Name */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-sm font-semibold text-gray-800">First Name</label>
                <div className="relative">
                  <input
                    type="text"
                    name="first_name"
                    value={form?.first_name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="First Name"
                    className={`w-full h-11 px-3 pr-9 rounded-xl border bg-white text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 transition-all ${getFieldClass(firstNameValid, touched?.first_name)}`}
                  />
                  <SuccessIcon show={touched?.first_name && firstNameValid} />
                </div>
                <FieldError show={touched?.first_name && !firstNameValid} message="First name is required" />
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-semibold text-gray-800">Last Name</label>
                <div className="relative">
                  <input
                    type="text"
                    name="last_name"
                    value={form?.last_name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Last Name"
                    className={`w-full h-11 px-3 pr-9 rounded-xl border bg-white text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 transition-all ${getFieldClass(lastNameValid, touched?.last_name)}`}
                  />
                  <SuccessIcon show={touched?.last_name && lastNameValid} />
                </div>
                <FieldError show={touched?.last_name && !lastNameValid} message="Last name is required" />
              </div>
            </div>

            {/* Organization Name */}
            <div className="space-y-1.5">
              <label className="text-sm font-semibold text-gray-800">Organization Name</label>
              <div className="relative">
                <Icon name="Building2" size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2" style={{color:'#5b4fcf'}} />
                <input
                  type="text"
                  name="organization_name"
                  value={form?.organization_name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Organization Name"
                  className={`w-full h-11 pl-10 pr-9 rounded-xl border bg-white text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 transition-all ${getFieldClass(orgNameValid, touched?.organization_name)}`}
                />
                <SuccessIcon show={touched?.organization_name && orgNameValid} />
              </div>
              <FieldError show={touched?.organization_name && !orgNameValid} message="Organization name is required" />
            </div>

            {/* Email */}
            <div className="space-y-1.5">
              <label className="text-sm font-semibold text-gray-800">Email Address</label>
              <div className="relative">
                <Icon name="Mail" size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2" style={{color:'#5b4fcf'}} />
                <input
                  type="text"
                  name="email_address"
                  value={form?.email_address}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Email Address"
                  className={`w-full h-11 pl-10 pr-9 rounded-xl border bg-white text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 transition-all ${getFieldClass(emailValid, touched?.email_address)}`}
                />
                <SuccessIcon show={touched?.email_address && emailValid} />
              </div>
              <FieldError show={touched?.email_address && !emailValid} message="Please enter a valid email address" />
            </div>

            {/* Phone */}
            <div className="space-y-1.5">
              <label className="text-sm font-semibold text-gray-800">Phone Number</label>
              <div className="relative">
                <Icon name="Phone" size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2" style={{color:'#5b4fcf'}} />
                <input
                  type="tel"
                  name="phone"
                  value={form?.phone}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Phone Number"
                  className={`w-full h-11 pl-10 pr-9 rounded-xl border bg-white text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 transition-all ${getFieldClass(phoneValid, touched?.phone)}`}
                />
                <SuccessIcon show={touched?.phone && phoneValid} />
              </div>
              <FieldError show={touched?.phone && !phoneValid} message="Phone number is required" />
            </div>

            {/* Password */}
            <div className="space-y-1.5">
              <label className="text-sm font-semibold text-gray-800">Password</label>
              <div className="relative">
                <Icon name="Lock" size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2" style={{color:'#5b4fcf'}} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={form?.password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Create password"
                  className={`w-full h-11 pl-10 pr-10 rounded-xl border bg-white text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 transition-all ${getFieldClass(passwordValid, touched?.password)}`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={16} />
                </button>
              </div>
              {(touched?.password || form?.password?.length > 0) && (
                <div className="grid grid-cols-2 gap-1 mt-2">
                  {passwordRules?.map((rule, i) => {
                    const passed = rule?.test(form?.password);
                    return (
                      <div key={i} className="flex items-center space-x-1.5">
                        <Icon name={passed ? 'CheckCircle2' : 'XCircle'} size={12}
                          className={passed ? 'text-green-500 flex-shrink-0' : 'text-red-400 flex-shrink-0'} />
                        <span className={`text-xs ${passed ? 'text-green-600' : 'text-red-400'}`}>{rule?.label}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Confirm Password */}
            <div className="space-y-1.5">
              <label className="text-sm font-semibold text-gray-800">Confirm Password</label>
              <div className="relative">
                <Icon name="Lock" size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2" style={{color:'#5b4fcf'}} />
                <input
                  type="password"
                  name="confirm_password"
                  value={form?.confirm_password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Confirm password"
                  className={`w-full h-11 pl-10 pr-9 rounded-xl border bg-white text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 transition-all ${getFieldClass(confirmValid, touched?.confirm_password)}`}
                />
                <SuccessIcon show={touched?.confirm_password && confirmValid} />
              </div>
              {touched?.confirm_password && !confirmValid && (
                <p className="text-xs text-red-500 flex items-center space-x-1 mt-1">
                  <Icon name="AlertCircle" size={12} className="flex-shrink-0" />
                  <span>{form?.confirm_password?.length === 0 ? 'Please confirm your password' : 'Passwords do not match'}</span>
                </p>
              )}
            </div>

            {/* API Error */}
            {error && (
              <div className="flex items-center space-x-2 p-3 bg-red-50 border border-red-200 rounded-xl">
                <Icon name="AlertCircle" size={16} className="text-red-500 flex-shrink-0" />
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading || !allValid}
              className="w-full h-12 rounded-xl text-sm font-semibold text-white shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 transition-all mt-2"
              style={{ background: 'linear-gradient(135deg, #5b4fcf 0%, #4a6bc4 100%)' }}
            >
              {loading
                ? <><Icon name="Loader2" size={16} className="animate-spin" /><span>Creating account...</span></>
                : <span>Create Account</span>}
            </button>

            <p className="text-center text-sm text-gray-500">
              Already have an account?{' '}
              <button type="button" onClick={() => navigate('/login')} className="font-medium hover:underline" style={{color:'#5b4fcf'}}>Sign in</button>
            </p>
          </form>
        )}
      </div>
    </AuthBackground>
  );
};

export default SignUpPage;
